// Presentational section + per-row checkbox list for the Build-from-Documents 5B review
// (s98). Mirrors the FanOutReviewModal checkbox pattern, but adds a section-level
// checkbox (toggles all its rows) and is fully controlled — the host owns selection. One
// document's fan-out sections at a time. Selection = sectionId → selected row indexes.

import type { FanOutSection } from "@/components/FanOutBanner";

interface FanOutSectionChecklistProps {
  sections: FanOutSection[];
  /** sectionId → selected row indexes. */
  selected: Record<string, number[]>;
  onToggleSection: (sectionId: string) => void;
  onToggleRow: (sectionId: string, rowIndex: number) => void;
}

export function FanOutSectionChecklist({ sections, selected, onToggleSection, onToggleRow }: FanOutSectionChecklistProps) {
  return (
    <div className="space-y-3">
      {sections.map((section) => {
        const sel = selected[section.sectionId] ?? [];
        const rowCount = section.rows.length;
        const allOn = rowCount > 0 && sel.length === rowCount;
        const partial = sel.length > 0 && sel.length < rowCount;
        return (
          <div key={section.sectionId} className="border rounded-lg overflow-hidden" data-testid={`review-section-${section.sectionSlug}`}>
            <label className="flex items-center gap-2 px-3 py-2 bg-muted/40 cursor-pointer">
              <input
                type="checkbox"
                className="h-4 w-4 shrink-0 accent-sage-600"
                checked={allOn}
                ref={(el) => { if (el) el.indeterminate = partial; }}
                onChange={() => onToggleSection(section.sectionId)}
                data-testid={`checkbox-review-section-${section.sectionSlug}`}
              />
              <span className="text-sm font-medium flex-1">{section.sectionName}</span>
              <span className="text-xs text-muted-foreground">
                {sel.length}/{rowCount} record{rowCount !== 1 ? "s" : ""}
              </span>
            </label>

            <div className="px-3 py-2 space-y-3">
              {section.rows.map((row, i) => {
                const checked = sel.includes(i);
                return (
                  <label
                    key={i}
                    className="flex items-start gap-2 cursor-pointer"
                    data-testid={`review-record-${section.sectionSlug}-${i}`}
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 shrink-0 accent-sage-600"
                      checked={checked}
                      onChange={() => onToggleRow(section.sectionId, i)}
                      data-testid={`checkbox-review-record-${section.sectionSlug}-${i}`}
                    />
                    <div className="flex-1 space-y-1">
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Record {i + 1}</p>
                      {row.fields.map((f) => (
                        <div key={f.fieldId} className="flex items-start gap-2 text-sm">
                          <span className="text-muted-foreground w-40 shrink-0">{f.name}</span>
                          <span className="flex-1 break-words">{f.value}</span>
                        </div>
                      ))}
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
